import { useCallback, useEffect, useRef, useState } from 'react';
import { format } from 'date-fns';
import Hls from 'hls.js';
import type { SensorAlertBundle } from '@mark43/cad-api';
import { Box, Flex, Icon, Text, VStack } from '@cad-arc';
import { logDebug } from '../monitoring/log';
import { SENSOR_HLS_BACK_BUFFER_SECONDS } from './constants';
import { formatSensorAlertClockTime, getSensorAlertTags } from './helpers';
import { buildHlsUrl, useSensorStreamToken } from './use-sensor-stream-token';
import {
    FullScreenButton,
    ReturnToLiveButton,
    ScrubberTick,
    TransportButton,
    VideoModeBadge,
    VideoReadout,
    VideoScrubber,
} from './video-player-chrome';

const SKIP_SECONDS = 10;
const PLAYBACK_RATES = [0.5, 1, 2] as const;
/** The design seeks to two seconds before an event, so its start is not already past. */
const TICK_SEEK_LEAD_SECONDS = 2;

type CameraVideoProps = {
    sensorId: string | undefined;
    /**
     * Newest alert thumbnail for this camera, shown when live video is unavailable. Served with a
     * permissive CORP header and no auth, so a plain `<img>` works — but the host 403s on HEAD, so
     * nothing here may preflight it.
     */
    fallbackImageUrl?: string;
    /** Placed on the scrubber as ticks, for alerts that fall inside the seekable window. */
    alerts?: SensorAlertBundle[];
};

/** `4:12`, how the design writes an offset from live — `4:12 BEHIND`, `-4:12`. */
export function formatBehindLive(seconds: number): string {
    const wholeSeconds = Math.max(0, Math.round(seconds));
    const minutes = Math.floor(wholeSeconds / 60);
    return `${minutes}:${String(wholeSeconds % 60).padStart(2, '0')}`;
}

function CameraVideoPlaceholder({
    fallbackImageUrl,
    message,
}: {
    fallbackImageUrl?: string;
    message: string;
}): React.ReactElement {
    return (
        /* Always 16:9, like the video it stands in for: the camera panel pins this zone, and a
           placeholder sized by whatever image loaded (or failed to) moved everything below it. */
        <Box
            position="relative"
            width="100%"
            backgroundColor="surface.canvas"
            style={{ aspectRatio: '16 / 9' }}
        >
            {fallbackImageUrl ? (
                <img
                    src={fallbackImageUrl}
                    alt="Most recent sensor event"
                    style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
                />
            ) : (
                <Flex height="100%" alignItems="center" justifyContent="center">
                    {/* `icon.default` — Arc has no `icon.muted`, and an unknown token renders nothing. */}
                    <Icon icon="Camera" size="lg" color="icon.default" />
                </Flex>
            )}
            {/* Over the frame rather than under it, so the zone stays exactly 16:9. */}
            <Flex
                position="absolute"
                left={0}
                right={0}
                bottom={0}
                padding={1}
                gap={1}
                alignItems="center"
                justifyContent="center"
                backgroundColor="surface.foreground"
            >
                <Text variant="caption" color="text.secondary">
                    {message}
                </Text>
            </Flex>
        </Box>
    );
}

/** `-mm:ss`, the depth of the seekable window shown at the left of the bar. */
function formatWindowDepth(seconds: number): string {
    const wholeSeconds = Math.max(0, Math.floor(seconds));
    const minutes = Math.floor(wholeSeconds / 60);
    return `-${minutes}:${String(wholeSeconds % 60).padStart(2, '0')}`;
}

export function CameraVideo({
    sensorId,
    fallbackImageUrl,
    alerts = [],
}: CameraVideoProps): React.ReactElement {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const hlsRef = useRef<Hls | null>(null);
    const { data: token, isLoading, isError } = useSensorStreamToken();
    const [hasPlaybackError, setHasPlaybackError] = useState(false);
    const [streamClock, setStreamClock] = useState<string | undefined>(undefined);
    const [playback, setPlayback] = useState({ currentTime: 0, windowStart: 0, windowEnd: 0 });
    const [isPaused, setIsPaused] = useState(false);
    const [playbackRate, setPlaybackRate] = useState(1);
    /**
     * Mode is state the user owns, not something inferred from playback position.
     *
     * It used to be derived from how far behind the live edge playback sat, which bounced between
     * modes on its own: HLS runs a segment or two behind live by design and that gap drifts by a
     * second at a time, so the badge changed with nobody touching anything. Now only a seek enters
     * reviewing, and only Return to live leaves it — including when playback runs forward and
     * reaches the live edge by itself.
     */
    const [isReviewing, setIsReviewing] = useState(false);

    const hlsUrl = buildHlsUrl(token, sensorId);

    useEffect(
        function attachHlsToVideoElement() {
            const videoElement = videoRef.current;
            if (!videoElement || !hlsUrl) {
                return;
            }

            setHasPlaybackError(false);

            // Safari plays HLS natively; everywhere else needs hls.js. `hls.js` is an explicit
            // dependency rather than going through vidstack, whose HLS provider lazy-loads the
            // library from a CDN at runtime.
            if (!Hls.isSupported()) {
                if (videoElement.canPlayType('application/vnd.apple.mpegurl')) {
                    videoElement.src = hlsUrl;
                    return;
                }
                setHasPlaybackError(true);
                return;
            }

            const hls = new Hls({
                enableWorker: true,
                backBufferLength: SENSOR_HLS_BACK_BUFFER_SECONDS,
            });
            hls.on(Hls.Events.ERROR, (_event, data) => {
                if (data.fatal) {
                    // The URL carries the streaming JWT in its query string, so log the failure
                    // classification only — never the url, and never the sensor's address.
                    logDebug('sensor camera hls fatal error', {
                        errorType: data.type,
                        errorDetails: data.details,
                    });
                    setHasPlaybackError(true);
                    hls.destroy();
                }
            });
            hls.loadSource(hlsUrl);
            hls.attachMedia(videoElement);
            hlsRef.current = hls;

            return function detachHls() {
                hlsRef.current = null;
                hls.destroy();
            };
        },
        [hlsUrl]
    );

    /**
     * Clock for the frame on screen, read from the stream rather than the local machine:
     * `playingDate` is hls.js resolving the current playback position against the playlist's
     * `EXT-X-PROGRAM-DATE-TIME`. It is null when the vendor's playlist carries no such tag, in
     * which case the chip is hidden — a wall clock here would look like stream time and be wrong
     * by however far playback has drifted behind live.
     */
    const handleTimeUpdate = useCallback(function updateReadouts() {
        const playingDate = hlsRef.current?.playingDate;
        const nextClock = playingDate ? format(playingDate, 'HH:mm:ss') : undefined;
        // `timeupdate` fires several times a second; React bails out of the re-render when the
        // formatted second has not changed, so this costs one render per second per open panel.
        setStreamClock((currentClock) => (currentClock === nextClock ? currentClock : nextClock));

        // `seekable` is the DVR window the playlist actually offers. It is the source of truth for
        // how far back you can go — not the stream URL's parameters.
        const videoElement = videoRef.current;
        if (videoElement && videoElement.seekable.length > 0) {
            setPlayback({
                currentTime: videoElement.currentTime,
                windowStart: videoElement.seekable.start(0),
                windowEnd: videoElement.seekable.end(0),
            });
        }
    }, []);

    /** The seek primitive. Clamps to the seekable window and leaves mode alone. */
    const seekToSeconds = useCallback(function seekToSeconds(seconds: number) {
        const videoElement = videoRef.current;
        if (!videoElement || videoElement.seekable.length === 0) {
            return;
        }
        const windowStart = videoElement.seekable.start(0);
        const windowEnd = videoElement.seekable.end(0);
        videoElement.currentTime = Math.min(Math.max(seconds, windowStart), windowEnd);
    }, []);

    /** Every user-initiated seek — the bar, a tick, the skip buttons — enters reviewing. */
    const reviewSeekToSeconds = useCallback(
        function reviewSeekToSeconds(seconds: number) {
            setIsReviewing(true);
            seekToSeconds(seconds);
        },
        [seekToSeconds]
    );

    const returnToLive = useCallback(function returnToLive() {
        const videoElement = videoRef.current;
        if (!videoElement) {
            return;
        }
        // `liveSyncPosition` is hls.js's own target for the live edge; `seekable.end` is the
        // fallback when it has not computed one yet.
        const liveEdge =
            hlsRef.current?.liveSyncPosition ??
            (videoElement.seekable.length > 0 ? videoElement.seekable.end(0) : undefined);
        if (liveEdge !== undefined) {
            videoElement.currentTime = liveEdge;
        }
        setIsReviewing(false);
        void videoElement.play();
        setIsPaused(false);
    }, []);

    const cyclePlaybackRate = useCallback(function cyclePlaybackRate() {
        const videoElement = videoRef.current;
        if (!videoElement) {
            return;
        }
        setPlaybackRate((currentRate) => {
            const nextRate =
                PLAYBACK_RATES[
                    (PLAYBACK_RATES.indexOf(currentRate as 1) + 1) % PLAYBACK_RATES.length
                ];
            videoElement.playbackRate = nextRate;
            return nextRate;
        });
    }, []);

    const togglePlayback = useCallback(function togglePlayback() {
        const videoElement = videoRef.current;
        if (!videoElement) {
            return;
        }
        if (videoElement.paused) {
            void videoElement.play();
            setIsPaused(false);
        } else {
            videoElement.pause();
            setIsPaused(true);
        }
    }, []);

    const toggleFullScreen = useCallback(function toggleFullScreen() {
        // The container rather than the video element, so our chrome comes with it.
        const container = containerRef.current;
        if (!container) {
            return;
        }
        if (document.fullscreenElement) {
            void document.exitFullscreen();
        } else {
            void container.requestFullscreen();
        }
    }, []);

    // `Esc` already exits full screen natively, so only `F` needs binding.
    const handleKeyDown = useCallback(
        function handleFullScreenShortcut(event: React.KeyboardEvent<HTMLDivElement>) {
            if (event.key === 'f' || event.key === 'F') {
                event.preventDefault();
                toggleFullScreen();
            }
        },
        [toggleFullScreen]
    );

    if (isLoading) {
        return <CameraVideoPlaceholder fallbackImageUrl={fallbackImageUrl} message="Connecting…" />;
    }

    // Any token failure lands here, which is the state to expect on an environment where the
    // Verkada host property is unset or the token fix has not been redeployed yet.
    if (isError || !hlsUrl) {
        return (
            <CameraVideoPlaceholder
                fallbackImageUrl={fallbackImageUrl}
                message="Live feed unavailable"
            />
        );
    }

    if (hasPlaybackError) {
        return (
            <CameraVideoPlaceholder
                fallbackImageUrl={fallbackImageUrl}
                message="Live feed could not be played"
            />
        );
    }

    const windowDepthSeconds = Math.max(0, playback.windowEnd - playback.windowStart);
    const behindLiveSeconds = Math.max(0, playback.windowEnd - playback.currentTime);
    const mode = isReviewing ? 'reviewing' : 'live';
    // Pinned to the right edge in live mode: there is nothing ahead of you, and a handle creeping
    // about would imply otherwise.
    const playedFraction =
        !isReviewing || windowDepthSeconds <= 0
            ? 1
            : (playback.currentTime - playback.windowStart) / windowDepthSeconds;

    /*
     * Ticks are placed by wall clock against the seekable window, so an alert only gets one if it
     * really falls inside what the stream lets you seek to. Nothing is drawn at an invented
     * position just to populate the bar.
     */
    const liveEdgeWallClockMs = hlsRef.current?.playingDate
        ? hlsRef.current.playingDate.getTime() + behindLiveSeconds * 1000
        : undefined;
    const ticks: ScrubberTick[] =
        liveEdgeWallClockMs === undefined || windowDepthSeconds <= 0
            ? []
            : alerts.flatMap((alert) => {
                  const alertMs = alert.createdDateUtc ? Date.parse(alert.createdDateUtc) : Number.NaN;
                  if (Number.isNaN(alertMs)) {
                      return [];
                  }
                  const secondsBehindLive = (liveEdgeWallClockMs - alertMs) / 1000;
                  if (secondsBehindLive < 0 || secondsBehindLive > windowDepthSeconds) {
                      return [];
                  }
                  return [
                      {
                          position: 1 - secondsBehindLive / windowDepthSeconds,
                          label: `${getSensorAlertTags(alert)[0] ?? 'Sensor event'} · ${formatSensorAlertClockTime(
                              alert.createdDateUtc
                          )}`,
                          seekToSeconds:
                              playback.windowEnd - secondsBehindLive - TICK_SEEK_LEAD_SECONDS,
                      },
                  ];
              });

    return (
        <Box
            ref={containerRef}
            backgroundColor="surface.canvas"
            tabIndex={0}
            onKeyDown={handleKeyDown}
        >
            {/* Only the badges sit over the video. The controls are a footer beneath it, so they
                are never floating in the middle of a letterboxed frame — and the video keeps its
                own aspect ratio rather than being fitted into a box with black bars. */}
            <Box position="relative">
                {/* Bare element: every control is ours, because native controls expose no
                    supported styling surface. Hidden from Session Replay, which records `poster`
                    and `src` verbatim (it masks only <img> sources) and skips `beforeSend`: the
                    poster is the alert image URL, whose query string is a live credential. */}
                <video
                    ref={videoRef}
                    data-testid="SENSOR_PANEL_VIDEO"
                    data-dd-privacy="hidden"
                    autoPlay
                    muted
                    playsInline
                    poster={fallbackImageUrl}
                    onTimeUpdate={handleTimeUpdate}
                    onClick={togglePlayback}
                    style={{ width: '100%', display: 'block', cursor: 'pointer' }}
                />
                <Flex
                    position="absolute"
                    top={2}
                    left={2}
                    gap={1}
                    pointerEvents="none"
                    flexWrap="wrap"
                >
                    <VideoModeBadge
                        mode={mode}
                        suffix={
                            isReviewing ? `${formatBehindLive(behindLiveSeconds)} BEHIND` : undefined
                        }
                    />
                    {streamClock ? <VideoReadout>{streamClock}</VideoReadout> : undefined}
                </Flex>
                {/* Only rendered when there is something to return from. */}
                {isReviewing ? (
                    <Box position="absolute" top={2} right={2}>
                        <ReturnToLiveButton onClick={returnToLive} />
                    </Box>
                ) : undefined}
            </Box>

            {/* Always rendered, so the anatomy never moves and nothing has to be hovered for.
                Black rather than `surface.foreground`: the grey read as a panel seam under the
                video instead of part of the player. */}
            <VStack
                align="stretch"
                spacing={0}
                paddingX={2}
                paddingY={1}
                style={{ background: '#000000' }}
            >
                <Flex alignItems="center" gap={2}>
                    <Text variant="caption" fontFamily="mono">
                        {formatWindowDepth(windowDepthSeconds)}
                    </Text>
                    <VideoScrubber
                        playedFraction={playedFraction}
                        ticks={ticks}
                        mode={mode}
                        onSeekToFraction={(fraction) =>
                            reviewSeekToSeconds(
                                playback.windowStart + fraction * windowDepthSeconds
                            )
                        }
                        onSeekToSeconds={reviewSeekToSeconds}
                    />
                    <Text variant="caption" fontFamily="mono">
                        {/* A live stream has no end to count toward, so the right-hand readout is
                            the mode; backtracked, it is how far behind you are. */}
                        {isReviewing ? `-${formatBehindLive(behindLiveSeconds)}` : 'LIVE'}
                    </Text>
                </Flex>
                {/* Anatomy never moves: transport left, full screen right, in both modes. */}
                <Flex alignItems="center" gap={1}>
                    <TransportButton label={isPaused ? 'Play' : 'Pause'} onClick={togglePlayback}>
                        {isPaused ? '▶' : '❙❙'}
                    </TransportButton>
                    <TransportButton
                        label="Back 10 seconds"
                        isDisabled={playback.currentTime - SKIP_SECONDS < playback.windowStart}
                        onClick={() => reviewSeekToSeconds(playback.currentTime - SKIP_SECONDS)}
                    >
                        −10 s
                    </TransportButton>
                    <TransportButton
                        label="Forward 10 seconds"
                        isDisabled={!isReviewing}
                        onClick={() => reviewSeekToSeconds(playback.currentTime + SKIP_SECONDS)}
                    >
                        +10 s
                    </TransportButton>
                    <TransportButton label="Playback speed" onClick={cyclePlaybackRate}>
                        {`${playbackRate}×`}
                    </TransportButton>
                    <Box marginLeft="auto">
                        <FullScreenButton onClick={toggleFullScreen} />
                    </Box>
                </Flex>
            </VStack>
        </Box>
    );
}
